import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiTag,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import customerService from '../services/customerService';
import Button from '../components/Button';
import './DetailView.css';

const CustomerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await customerService.getById(id);
      setCustomer(response.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <div className="detail-view-container">
      <div className="detail-header">
        <button onClick={() => navigate('/customers')} className="back-button">
          <FiArrowLeft /> Back to Customers
        </button>
        <Link to={`/customers/${id}/edit`}>
          <Button variant="primary" icon={<FiEdit />}>
            Edit Customer
          </Button>
        </Link>
      </div>

      <div className="detail-card">
        <div className="detail-hero">
          <div className="hero-avatar">
            <FiUser />
          </div>
          <div className="hero-info">
            <h1 className="hero-title">{customer.name}</h1>
            <p className="hero-subtitle">
              {customer.customerType
                ? `${customer.customerType.charAt(0).toUpperCase()}${customer.customerType.slice(1)} customer`
                : 'Customer'}
            </p>
            <span className={customer.isActive ? 'badge badge-success' : 'badge badge-gray'}>
              {customer.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="detail-sections">
          <div className="detail-section">
            <h3 className="section-title">Contact Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <FiPhone className="info-icon" />
                <div>
                  <span className="info-label">Primary Phone</span>
                  <span className="info-value">{customer.phone}</span>
                </div>
              </div>
              {customer.alternatePhone && (
                <div className="info-item">
                  <FiPhone className="info-icon" />
                  <div>
                    <span className="info-label">Alternate Phone</span>
                    <span className="info-value">{customer.alternatePhone}</span>
                  </div>
                </div>
              )}
              {customer.email && (
                <div className="info-item">
                  <FiMail className="info-icon" />
                  <div>
                    <span className="info-label">Email</span>
                    <span className="info-value">{customer.email}</span>
                  </div>
                </div>
              )}
              {customer.gstNumber && (
                <div className="info-item">
                  <FiTag className="info-icon" />
                  <div>
                    <span className="info-label">GST Number</span>
                    <span className="info-value">{customer.gstNumber}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(customer.address?.street ||
            customer.address?.city ||
            customer.address?.state ||
            customer.address?.zipCode) && (
            <div className="detail-section">
              <h3 className="section-title">Address</h3>
              <div className="info-grid">
                <div className="info-item">
                  <FiMapPin className="info-icon" />
                  <div>
                    <span className="info-label">Service Address</span>
                    <span className="info-value">
                      {[customer.address?.street, customer.address?.city, customer.address?.state, customer.address?.zipCode]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {customer.tags && customer.tags.length > 0 && (
            <div className="detail-section">
              <h3 className="section-title">Tags</h3>
              <div className="expertise-tags">
                {customer.tags.map((tag, idx) => (
                  <span key={idx} className="expertise-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {customer.notes && (
            <div className="detail-section">
              <h3 className="section-title">Notes</h3>
              <p className="notes-text">{customer.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerView;

